import { inject, injectable } from "../injection";
import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import { observable, when, action, makeObservable } from "mobx";
import { windowAsAny } from "../windowAsAny/windowAsAny";

@injectable("singleton")
export class AnalyticsService {
    @observable private isReady = false;
    @observable private isTrackingEnabled = false;

    constructor(
        @inject(LoggedInUserStore) private loggedInUserStore: LoggedInUserStore
    ) {
        makeObservable(this);

        void this.initialise();
    }

    public identifyUser(): void {
        void this.ifTrackingEnabled(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            windowAsAny().aptrinsic(
                "identify",
                {
                    // user fields
                    id: `${this.loggedInUserStore.forename}-${this.loggedInUserStore.surname}-${this.loggedInUserStore.clientName}-${this.loggedInUserStore.id.toString()}`,
                    email: this.loggedInUserStore.emailAddress,
                    firstName: this.loggedInUserStore.forename,
                    lastName: this.loggedInUserStore.surname,
                    role:
                        this.loggedInUserStore.jobTitleType != null
                            ? ((
                                  RedDeer.Contracts.Enums.UserJobTitleType[
                                      this.loggedInUserStore.jobTitleType
                                  ] as string | undefined
                              )?.replace(/([A-Z][a-z])/g, " $1") ?? "")
                            : ""
                },
                {
                    // account fields
                    id: this.loggedInUserStore.clientName,
                    name: this.loggedInUserStore.clientName
                }
            );
        });
    }

    private async initialise() {
        await when(() => this.loggedInUserStore.isReady);

        if (
            this.loggedInUserStore.isImpersonating ||
            !this.loggedInUserStore.enableUsageAnalytics
        ) {
            this.setTrackingEnabled(false);
            return;
        }

        await this.waitForAptrinsic();
        this.setTrackingEnabled(true);
    }

    private waitForAptrinsic(): Promise<void> {
        return new Promise<void>(resolve => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (windowAsAny().aptrinsic) {
                resolve();
                return;
            }
            const interval = setInterval(() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (windowAsAny().aptrinsic) {
                    resolve();
                    clearInterval(interval);
                }
            }, 500);
        });
    }

    @action private setTrackingEnabled(value: boolean): void {
        this.isTrackingEnabled = value;
        this.isReady = true;
    }

    private async ifTrackingEnabled(callback: () => void) {
        await when(() => this.isReady);
        if (this.isTrackingEnabled) {
            callback();
        }
    }
}
